import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, DragOverlay
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft, Plus, Edit2, Trash2, X, Loader2, GripVertical,
  FileText, Bell, Upload, Send, AlertCircle, CheckCircle2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  todo: { label: 'Te Doen', color: 'bg-slate-500', bgColor: 'bg-slate-500/10' },
  in_progress: { label: 'In Uitvoering', color: 'bg-blue-500', bgColor: 'bg-blue-500/10' },
  review: { label: 'Review', color: 'bg-yellow-500', bgColor: 'bg-yellow-500/10' },
  done: { label: 'Voltooid', color: 'bg-green-500', bgColor: 'bg-green-500/10' }
};

// Sortable Task Card Component
const SortableTaskCard = ({ task, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow group"
    >
      <div className="flex items-start gap-2">
        <button
          {...attributes}
          {...listeners}
          className="p-1 text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900">{task.title}</p>
          {task.description && (
            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{task.description}</p>
          )}
        </div>
        
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(task)}
            className="p-1 text-slate-400 hover:text-blue-600 rounded"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(task.id)}
            className="p-1 text-slate-400 hover:text-red-600 rounded"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Kanban Column Component
const KanbanColumn = ({ column, tasks, onAddTask, onEditTask, onDeleteTask }) => {
  return (
    <div className={`rounded-xl p-4 ${statusConfig[column].bgColor}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${statusConfig[column].color}`} />
          <h3 className="font-medium text-slate-900">{statusConfig[column].label}</h3>
          <span className="text-sm text-slate-500">({tasks.length})</span>
        </div>
        <button
          onClick={() => onAddTask(column)}
          className="p-1 text-slate-400 hover:text-blue-600 hover:bg-white rounded transition-all"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      
      <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2 min-h-[150px]">
          {tasks.map(task => (
            <SortableTaskCard
              key={task.id}
              task={task}
              onEdit={onEditTask}
              onDelete={onDeleteTask}
            />
          ))}
        </div>
      </SortableContext>
    </div>
  );
};

const PortalProjectAdmin = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('kanban');
  
  // Task Modal State
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', status: 'todo' });
  
  // Update Modal State
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateForm, setUpdateForm] = useState({ title: '', message: '', is_important: false });
  
  // File Upload State
  const [isUploading, setIsUploading] = useState(false);
  
  // Drag and Drop
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    if (projectId) {
      fetchProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        toast.error('Project niet gevonden');
        navigate('/portal-admin');
        return;
      }
      
      const data = await response.json();
      setProject(data);
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    } finally {
      setIsLoading(false);
    }
  };

  // Task Functions
  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTask) {
        const response = await fetch(
          `${API_URL}/api/portal/projects/${projectId}/tasks/${editingTask.id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify(taskForm)
          }
        );
        
        if (response.ok) {
          toast.success('Taak bijgewerkt');
          fetchProject();
          closeTaskModal();
        }
      } else {
        const response = await fetch(
          `${API_URL}/api/portal/projects/${projectId}/tasks`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify(taskForm)
          }
        );
        
        if (response.ok) {
          toast.success('Taak aangemaakt');
          fetchProject();
          closeTaskModal();
        }
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Weet je zeker dat je deze taak wilt verwijderen?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/portal/projects/${projectId}/tasks/${taskId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        toast.success('Taak verwijderd');
        fetchProject();
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (!over) return;
    
    const activeTask = project.tasks.find(t => t.id === active.id);
    if (!activeTask) return;
    
    // Find which column the task was dropped in
    const columns = ['todo', 'in_progress', 'review', 'done'];
    let newStatus = activeTask.status;
    
    // Check if dropped on another task
    const overTask = project.tasks.find(t => t.id === over.id);
    if (overTask) {
      newStatus = overTask.status;
    }
    
    // If status changed, update on server
    if (newStatus !== activeTask.status) {
      try {
        await fetch(`${API_URL}/api/portal/projects/${projectId}/tasks/move`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            task_id: active.id,
            new_status: newStatus,
            new_order: 0
          })
        });
        
        fetchProject();
      } catch (error) {
        // Error handled silently
      }
    }
  };

  const openAddTaskModal = (status) => {
    setEditingTask(null);
    setTaskForm({ title: '', description: '', status });
    setShowTaskModal(true);
  };

  const openEditTaskModal = (task) => {
    setEditingTask(task);
    setTaskForm({ title: task.title, description: task.description || '', status: task.status });
    setShowTaskModal(true);
  };

  const closeTaskModal = () => {
    setShowTaskModal(false);
    setEditingTask(null);
    setTaskForm({ title: '', description: '', status: 'todo' });
  };

  // Update Functions
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/portal/projects/${projectId}/updates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(updateForm)
      });
      
      if (response.ok) {
        toast.success('Update geplaatst');
        fetchProject();
        setShowUpdateModal(false);
        setUpdateForm({ title: '', message: '', is_important: false });
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    }
  };

  const handleDeleteUpdate = async (updateId) => {
    if (!window.confirm('Weet je zeker dat je deze update wilt verwijderen?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/portal/projects/${projectId}/updates/${updateId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        toast.success('Update verwijderd');
        fetchProject();
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    }
  };

  // File Functions
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Bestand is te groot (max 10MB)');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/portal/projects/${projectId}/files`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      
      if (response.ok) {
        toast.success('Bestand geüpload');
        fetchProject();
      } else {
        toast.error('Upload mislukt');
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Weet je zeker dat je dit bestand wilt verwijderen?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/portal/projects/${projectId}/files/${fileId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        toast.success('Bestand verwijderd');
        fetchProject();
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getTasksByStatus = (status) => {
    return project?.tasks?.filter(t => t.status === status) || [];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/portal-admin"
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{project.name}</h1>
            <p className="text-slate-600">{project.description || 'Geen beschrijving'}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200 pb-2">
          {[
            { id: 'kanban', label: 'Kanban Bord', icon: CheckCircle2 },
            { id: 'updates', label: 'Updates', icon: Bell },
            { id: 'files', label: 'Bestanden', icon: FileText }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'kanban' && (
            <motion.div
              key="kanban"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {['todo', 'in_progress', 'review', 'done'].map(status => (
                    <KanbanColumn
                      key={status}
                      column={status}
                      tasks={getTasksByStatus(status)}
                      onAddTask={openAddTaskModal}
                      onEditTask={openEditTaskModal}
                      onDeleteTask={handleDeleteTask}
                    />
                  ))}
                </div>
              </DndContext>
            </motion.div>
          )}

          {activeTab === 'updates' && (
            <motion.div
              key="updates"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="mb-4">
                <button
                  onClick={() => setShowUpdateModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Nieuwe Update
                </button>
              </div>
              
              {project.updates?.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
                  <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nog geen updates</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {project.updates?.map(update => (
                    <div
                      key={update.id}
                      className={`bg-white border rounded-xl p-4 ${
                        update.is_important ? 'border-yellow-300' : 'border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {update.is_important && (
                              <AlertCircle className="w-4 h-4 text-yellow-500" />
                            )}
                            <h4 className="font-medium text-slate-900">{update.title}</h4>
                            {!update.is_read && (
                              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-600 rounded-full">
                                Ongelezen
                              </span>
                            )}
                          </div>
                          <p className="text-slate-600 text-sm">{update.message}</p>
                          <p className="text-xs text-slate-400 mt-2">{formatDate(update.created_at)}</p>
                        </div>
                        
                        <button
                          onClick={() => handleDeleteUpdate(update.id)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'files' && (
            <motion.div
              key="files"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="mb-4">
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors cursor-pointer">
                  <Upload className="w-4 h-4" />
                  {isUploading ? 'Uploaden...' : 'Bestand Uploaden'}
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={isUploading}
                  />
                </label>
              </div>
              
              {project.files?.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nog geen bestanden</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {project.files?.map(file => (
                    <div
                      key={file.id}
                      className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-slate-100 rounded-lg">
                          <FileText className="w-6 h-6 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{file.original_name}</p>
                          <p className="text-sm text-slate-500">
                            {formatFileSize(file.file_size)} • {formatDate(file.uploaded_at)}
                          </p>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => handleDeleteFile(file.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Task Modal */}
        <AnimatePresence>
          {showTaskModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={closeTaskModal}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl p-6 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-slate-900">
                    {editingTask ? 'Taak Bewerken' : 'Nieuwe Taak'}
                  </h2>
                  <button onClick={closeTaskModal} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleTaskSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Titel *</label>
                    <input
                      type="text"
                      value={taskForm.title}
                      onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Beschrijving</label>
                    <textarea
                      value={taskForm.description}
                      onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                    <select
                      value={taskForm.status}
                      onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="todo">Te Doen</option>
                      <option value="in_progress">In Uitvoering</option>
                      <option value="review">Review</option>
                      <option value="done">Voltooid</option>
                    </select>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button type="button" onClick={closeTaskModal} className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50">
                      Annuleren
                    </button>
                    <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
                      {editingTask ? 'Opslaan' : 'Aanmaken'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Update Modal */}
        <AnimatePresence>
          {showUpdateModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={() => setShowUpdateModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl p-6 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-slate-900">Nieuwe Update</h2>
                  <button onClick={() => setShowUpdateModal(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleUpdateSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Titel *</label>
                    <input
                      type="text"
                      value={updateForm.title}
                      onChange={(e) => setUpdateForm({ ...updateForm, title: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Bericht *</label>
                    <textarea
                      value={updateForm.message}
                      onChange={(e) => setUpdateForm({ ...updateForm, message: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={4}
                      required
                    />
                  </div>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={updateForm.is_important}
                      onChange={(e) => setUpdateForm({ ...updateForm, is_important: e.target.checked })}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-700">Markeer als belangrijk</span>
                  </label>

                  <div className="flex gap-3 pt-4">
                    <button type="button" onClick={() => setShowUpdateModal(false)} className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50">
                      Annuleren
                    </button>
                    <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
                      Verzenden
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PortalProjectAdmin;
